import React from "react"
import { getLinkByPageKey, pageKeys } from "../utils/navHelper"
import commonStyles from "../pages/common-styles.module.scss"
import aboutMeStyles from "../pages/about-me.module.scss"

export const INDIVIDUAL_THERAPY_ID = "individual-therapy"
export const INDIVIDUAL_THERAPY_NAME = "Individual Therapy"
const INDIVIDUAL_THERAPY_NAME_COMPACT = "Individual"
export const RELATIONSHIPS_THERAPY_ID = "relationships-therapy"
export const RELATIONSHIPS_THERAPY_NAME = "Relationships Therapy"
const RELATIONSHIPS_THERAPY_NAME_COMPACT = "Relationship"
export const PATERNITY_FATHERHOOD_COACHING_ID = "paternity-fatherhood-coaching"
export const PATERNITY_FATHERHOOD_COACHING_NAME =
  "Paternity / Fatherhood Coaching"
const PATERNITY_FATHERHOOD_COACHING_NAME_COMPACT = "Paternity / Fatherhood"

const whatIOfferData = [
  {
    id: INDIVIDUAL_THERAPY_ID,
    name: INDIVIDUAL_THERAPY_NAME,
    compactName: INDIVIDUAL_THERAPY_NAME_COMPACT,
    text: (
      <>
        <h3 className={aboutMeStyles.subtitle}>
          Understanding What You're Looking For
        </h3>
        <p>
          My first priority is to understand what you're hoping for from our
          work together.
        </p>
        <p>
          My approach draws on several therapeutic models, and I adapt how I
          work to each person's needs and preferences. I use two main
          therapeutic approaches — Internal Family Systems (IFS) and Cognitive
          Behavioural Therapy (CBT).
        </p>
        <p>
          If you're curious, I've written a blog post exploring some of the
          tensions and potential synergies of combining these two approaches.
        </p>

        <h3 className={aboutMeStyles.subtitle}>My Therapeutic Approaches</h3>
        <h4>Cognitive Behavioural Therapy (CBT)</h4>
        <p>
          CBT helps you to learn that you are not your thoughts, and brings
          awareness to how your thoughts, emotions, and behaviours influence
          each other.
          <br />
          It invites us to examine and revise our thoughts and our behaviours,
          and teaches practical ways to make change.
        </p>

        <h4>Internal Family Systems (IFS)</h4>
        <p>
          IFS therapy complements this by assuming we are all made of multiple
          parts, each trying to serve us in their own way.
          <br />
          From an IFS perspective, even our most difficult thoughts or
          behaviours are parts of us trying, in their own way, to protect us
          from pain or vulnerability.
          <br />
          These protective roles can become rigid and extreme. Together, we
          would explore these parts of you, understand what they're protecting,
          and help them find new, less extreme ways to support you.
        </p>

        <h3 className={aboutMeStyles.subtitle}>The Importance of Connection</h3>
        <p>
          It can be interesting to explore different therapeutic approaches, but
          research consistently shows that what matters most is the quality of
          the relationship between therapist and client.
        </p>
        <p>
          Feeling safe, understood, and supported has a bigger impact than any
          specific method.¹ That's why looking for the right match in
          personality, style, values, and that gut feeling that someone 'just
          gets you' is more important than their therapeutic background.
        </p>
        <p>
          Starting therapy or contacting a therapist for the first time can be a
          daunting experience. I encourage you to take a look at my{" "}
          {getLinkByPageKey(pageKeys.ABOUT_ME)} page, which includes a video
          introduction that may help you get a sense of whether I am someone you
          think you could connect with.
        </p>

        <h3 className={aboutMeStyles.subtitle}>What Therapy Means to Me</h3>
        <blockquote className={commonStyles.quoteBlock}>
          <cite>
            Therapy isn't about being perfect or always happy. It's about having
            a space where every part of you is welcome — even the sad or anxious
            parts — and learning to offer that same unconditional acceptance
            towards yourself.
          </cite>
        </blockquote>

        <p>
          You can find more about my approach on the{" "}
          {getLinkByPageKey(pageKeys.FAQ)} page or my{" "}
          {getLinkByPageKey(pageKeys.BLOG)}. If you have questions or would like
          to explore working together, you can visit the{" "}
          {getLinkByPageKey(pageKeys.BOOK_A_SESSION)} page.
        </p>

        <div
          style={{
            borderBottom: "1px solid",
            borderColor: "rgba(128, 128, 128, 0.35)",
            color: "#808080",
            display: "flex",
            flexDirection: "column",
            lineHeight: "12pt",
            fontSize: "0.7rem",
            marginTop: "5rem",
            marginBottom: "-3rem"
          }}
        >
          <a
            name="ardito-raballino-2011"
            href="https://doi.org/10.3389/fpsyg.2011.00270"
            target="_blank"
            rel="noreferrer noopener"
            style={{
              color: "#808080",
              textDecoration: "none",
              fontSize: "0.7rem",
              lineHeight: "12pt"
            }}
          >
            1. Ardito & Raballino, 2011 https://doi.org/10.3389/fpsyg.2011.00270
          </a>
        </div>
      </>
    )
  },
  {
    id: RELATIONSHIPS_THERAPY_ID,
    name: RELATIONSHIPS_THERAPY_NAME,
    compactName: RELATIONSHIPS_THERAPY_NAME_COMPACT,
    text: (
      <>
        <h3 className={aboutMeStyles.subtitle}>Who I Work With</h3>
        <p>
          In my work as a relationships therapist, I mainly work with couples,
          but also with people in other types of relationships such as
          parent–child, siblings, friends, or work colleagues.
        </p>
        <p>
          In any of these cases, my role is to bring awareness to the
          underlying, repeating patterns of the relationship. Once we are aware
          of any patterns or dynamics that are harmful or limiting, we work
          together to find less painful and more harmonious alternatives.
        </p>

        <h3 className={aboutMeStyles.subtitle}>My Therapeutic Approach</h3>
        <p>
          My work in this area is mainly informed by my training as an IFIO
          (Intimacy from the Inside Out) therapist.
        </p>
        <p>
          IFIO is an approach developed by Toni Herbine Blank (
          <a href="https://www.toniherbineblank.com/">toniherbineblank.com</a>)
          and is the Internal Family Systems (IFS) approach to working with
          people in relationships.
        </p>
        <p>
          By learning to speak and listen in new ways, couples begin to feel
          more seen and heard, and can experience relational healing instead of
          wounding.
        </p>

        <h3 className={aboutMeStyles.subtitle}>
          When Relationship Therapy Can Help
        </h3>
        <p>
          Approaching a couples therapist can sometimes be a step taken to save
          a relationship from breakdown. It can also be a way of bringing more
          vitality and connection, or a way to end a relationship more
          compassionately — from a place of greater understanding, acceptance,
          and collaboration.
        </p>
        <p>
          Whatever the format or phase of your relationship, if what I am
          describing here sounds like something you would benefit from, get in
          touch via my {getLinkByPageKey(pageKeys.BOOK_A_SESSION)} page, or
          visit my {getLinkByPageKey(pageKeys.FAQ)} page where I address some
          common questions about working with couples.
        </p>
      </>
    )
  },
  {
    id: PATERNITY_FATHERHOOD_COACHING_ID,
    name: PATERNITY_FATHERHOOD_COACHING_NAME,
    compactName: PATERNITY_FATHERHOOD_COACHING_NAME_COMPACT,
    text: (
      <>
        <h3 className={aboutMeStyles.subtitle}>My Approach</h3>
        <p>
          The core of my approach to working as a paternity coach integrates my
          training in individual and couples therapy with my experience as a
          parent. This is supported by ongoing study in the psychology and
          biology of becoming a parent when you are not the one giving birth.
        </p>

        <h3 className={aboutMeStyles.subtitle}>Who I Work With</h3>
        <p>
          Most of my work is with people who identify as being a Dad, or who are
          about to become one.
        </p>
        <p>
          This work, however, is equally applicable to anyone of any gender
          identity who is about to become, or has recently become, a parent and
          who is not the birth partner.
        </p>

        <h3 className={aboutMeStyles.subtitle}>How We Work Together</h3>
        <p>
          To give more clarity about what I offer as a coach, I have organised
          my work into six areas of paternity.
        </p>
        <p>
          We can work through each of these areas, or select only the ones you
          want to prioritise. In any case, we would explore the chosen content
          at your pace, while also creating space at every step to reflect on
          what it means for you personally.
        </p>

        <h3 className={aboutMeStyles.subtitle}>Six Areas of Focus</h3>
        <ol>
          <li>
            <em>The Biology and Self-Determination of Fatherhood</em>
          </li>
          <li>
            <em>Taking Care of Ourselves</em>
          </li>
          <li>
            <em>Pregnancy and Birth</em>
          </li>
          <li>
            <em>Looking After Our Partner and Our Relationship with Them</em>
          </li>
          <li>
            <em>Relationship with Our Child</em>
          </li>
          <li>
            <em>Juggling Work and Home</em>
          </li>
        </ol>

        <p>
          As these categories suggest, the focus is on the period of pregnancy,
          birth, and post-partum.
        </p>
        <p>
          Although presented as distinct areas, they are all interrelated, and
          there isn't a fixed amount of time or sessions required for each. In a
          single session we could cover multiple areas, or we might spend
          several sessions exploring one area more deeply.
        </p>

        <h3 className={aboutMeStyles.subtitle}>
          What This Coaching Is (and Isn't)
        </h3>
        <p>
          This is not a course on how to raise a child, nor does it promote any
          particular approach to parenting.
        </p>
        <p>
          Instead, it is about connecting with the kind of parent you want to
          be, nurturing your relationship with your partner, and creating the
          best conditions for a strong, meaningful connection with your child.
        </p>

        <h3 className={aboutMeStyles.subtitle}>Next Steps</h3>
        <p>
          If you're interested in finding out more or would like to book a free
          introductory call, visit the{" "}
          {getLinkByPageKey(pageKeys.BOOK_A_SESSION)} page — or see if I address
          your questions in the {getLinkByPageKey(pageKeys.FAQ, false, "FAQs")}.
        </p>
      </>
    )
  }
]

export default whatIOfferData
